<template>
  <span
    v-if="show"
    :class="classes"
    :style="chipStyle"
    @click="emit('click', $event)"
  >
    <slot />
    <button
      v-if="close"
      type="button"
      class="ml-1 opacity-70 hover:opacity-100"
      aria-label="close"
      @click="onClose"
    >
      <UiIcon name="x" :size="16" />
    </button>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { resolveThemeColor } from './themeColor'
import UiIcon from './UiIcon.vue'

/** UiChip — Vuetify <v-chip> replacement.
 * `color` sets the background (solid) or the border+text (outlined), accepting
 * either a theme name (primary, warning, grey1, ...) resolved via --color-*,
 * or any raw CSS color (hex, rgb, named color, ...).
 * Dual Vue 2 / Vue 3 compatible. */
const props = withDefaults(
  defineProps<{
    color?: string
    outlined?: boolean
    small?: boolean
    close?: boolean
    modelValue?: boolean
    value?: boolean
  }>(),
  {
    color: undefined,
    outlined: false,
    small: false,
    close: false,
    modelValue: undefined,
    value: undefined,
  },
)

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
  (e: 'click:close'): void
  (e: 'update:modelValue', value: boolean): void
}>()

const show = computed(() => props.modelValue ?? props.value ?? true)
function onClose() {
  emit('update:modelValue', false)
  emit('click:close')
}

const resolvedColor = computed(() => resolveThemeColor(props.color))

/** Relative luminance per WCAG, for choosing readable text on solid chips. */
function luminance(hex: string): number {
  const m = hex.replace('#', '')
  const n =
    m.length === 3
      ? m
          .split('')
          .map((x) => x + x)
          .join('')
      : m
  const [r, g, b] = [0, 2, 4].map((i) =>
    Math.min(255, Math.max(0, parseInt(n.slice(i, i + 2), 16) || 0)),
  )
  const toLin = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b)
}

/**
 * For solid chips, pick a readable text color for the resolved background.
 * Only meaningful when `resolvedColor` is a concrete hex; for `var(--color-*)`
 * we fall back to a safe default (white) since we can't read the CSS var in JS.
 */
const textColor = computed(() => {
  const c = resolvedColor.value
  if (c == null) return undefined
  if (c.startsWith('#')) return luminance(c) > 0.5 ? '#111827' : '#ffffff'
  return undefined
})

const classes = computed(() =>
  [
    'inline-flex items-center rounded-full font-medium whitespace-nowrap px-3 py-1',
    props.small ? 'text-xs' : 'text-sm',
    props.color == null &&
      (props.outlined
        ? 'border border-gray-300 bg-transparent text-gray-700'
        : 'bg-gray-100 text-gray-800'),
    props.outlined && props.color != null ? 'border bg-transparent' : '',
  ].join(' '),
)

/** Inline style applies only when `color` is set; otherwise Tailwind classes win. */
const chipStyle = computed(() => {
  const c = resolvedColor.value
  if (c == null) return undefined
  if (props.outlined) {
    return {
      color: c,
      borderColor: c,
      backgroundColor: 'transparent',
    }
  }
  return {
    backgroundColor: c,
    color: textColor.value ?? '#ffffff',
    borderColor: c,
  }
})
</script>
